/**
 * Utility functions for level sound logic
 */

/**
 * Checks if the game is actively running
 * @param {Object} timeData - Current time data from the game
 * @returns {boolean} True if game is active and running
 */
export const isGameActive = (timeData) => {
    return timeData?.level && timeData?.event_state === "taking" && !timeData?.pause
}

/**
 * Detects if current transition involves break time
 * @param {string} currentLevel - Current level
 * @param {string} lastLevel - Previous level
 * @returns {boolean} True if either level is "break"
 */
export const isBreakTransition = (currentLevel, lastLevel) => {
    return currentLevel === "break" || lastLevel === "break"
}

/**
 * Detects admin interventions (resets, manual level changes)
 * @param {string} currentLevel - Current level
 * @param {string} lastLevel - Previous level
 * @returns {boolean} True if admin action detected
 */
export const isAdminIntervention = (currentLevel, lastLevel) => {
    const currentNum = parseInt(currentLevel) || 0
    const lastNum = parseInt(lastLevel) || 0
    
    const isResetToFirst = currentLevel === "1" && lastLevel !== null && lastLevel !== "1"
    const isGoingBackwards = lastLevel !== null && 
                            currentNum < lastNum && 
                            !isBreakTransition(currentLevel, lastLevel)
    
    return isResetToFirst || isGoingBackwards
}

/**
 * Validates if level progression is natural (upward)
 * @param {string} currentLevel - Current level
 * @param {string} lastLevel - Previous level
 * @returns {boolean} True if natural upward progression
 */
export const isNaturalLevelProgression = (currentLevel, lastLevel) => {
    if (!lastLevel || isBreakTransition(currentLevel, lastLevel)) return false
    
    const currentNum = parseInt(currentLevel) || 0
    const lastNum = parseInt(lastLevel) || 0
    
    return currentNum > lastNum
}

/**
 * Main decision function for whether to play level sound
 * @param {Object} timeData - Current time data
 * @param {Object} lastState - Previous level state
 * @returns {boolean} True if level sound should play
 */
export const shouldPlayLevelSound = (timeData, lastState) => {
    const { level: currentLevel, is_level_up: currentIsLevelUp, next_level } = timeData
    const { level: lastLevel, wasLevelUp: lastWasLevelUp } = lastState
    
    // Basic requirements
    const hasLevelChanged = lastLevel !== null && lastLevel !== currentLevel
    const isLevelUpFlagTransition = currentIsLevelUp && !lastWasLevelUp
    
    if (!hasLevelChanged || !isLevelUpFlagTransition) return false
    
    // Blocking conditions
    const isBreakTime = isBreakTransition(currentLevel, lastLevel)
    const isAdminAction = isAdminIntervention(currentLevel, lastLevel)
    const isFinalLevel = next_level === null || next_level === "0"
    const isNaturalProgression = isNaturalLevelProgression(currentLevel, lastLevel)
    
    return isNaturalProgression && !isBreakTime && !isAdminAction && !isFinalLevel
}
